
import type { ArenaDuel, ArenaInvitation, ArenaResult } from "../net/messages";
import { elem, styleInstaller } from "./chrome";

export interface ArenaActions {
  /** Accept or decline, naming the token of the challenge on screen. */
  onAnswer?: (token: number, accept: boolean) => void;
  /** Leave the seat this player is watching a match from. */
  onStopWatching?: () => void;
}

/**
 * `ArenaResult.outcome`. Wire values, and the server's alone -- nothing on this
 * side may construct one.
 */
export const ARENA_WON = 0;
export const ARENA_TIMEOUT = 1;
export const ARENA_WITHDRAWN = 2;

/** `ArenaDuel.identity`, from `MEMBER_IDENTITY` (`arena.h:5-11`). */
export const IDENTITY_DUELIST = 1;
export const IDENTITY_OBSERVER = 2;

export function describeResult(result: ArenaResult): string {
  // The server's own chat lines for these two draws (`arena.cpp:430`,
  // `:236`), not a paraphrase -- the score suffix on a win is dropped for the
  // same reason: `arena.cpp:755` announces only `"%s has won the duel."`,
  // never a score, because the score is already the scoreboard's job.
  if (result.outcome === ARENA_TIMEOUT) {
    return "The duel has finished because of a timeout.";
  }
  if (result.outcome === ARENA_WITHDRAWN) {
    return "The duel has finished, because your combatant vanished.";
  }
  const who = result.winner_name || "Somebody";
  return `${who} has won the duel.`;
}

/** `m:ss`, for the match clock. */
function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(whole / 60);
  return `${mins}:${String(whole % 60).padStart(2, "0")}`;
}

const STYLE_ID = "arena-style";

const STYLE = `
.arena {
  position: fixed;
  left: 50%;
  top: 12px;
  transform: translateX(-50%);
  z-index: 59;
  min-width: 260px;
  padding: 8px 14px 10px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.88);
  border: 1px solid rgba(255, 180, 120, 0.28);
  pointer-events: auto;
  font: 12px/1.4 system-ui, sans-serif;
  color: #d8dee6;
  text-align: center;
}
.arena[hidden] { display: none; }
.arena-title {
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #d8a475;
  margin-bottom: 4px;
}
.arena-score {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 10px;
  font-size: 14px;
}
.arena-fighter { min-width: 84px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.arena-fighter-you { color: #ffd27a; }
.arena-wins { font-size: 18px; font-variant-numeric: tabular-nums; }
.arena-versus { color: #8fa0b4; }
.arena-note { color: #8fa0b4; font-size: 11px; margin-top: 4px; }
.arena-break { color: #ffd27a; }
.arena-leave {
  margin-top: 6px;
  padding: 2px 12px;
  border-radius: 6px;
  border: 1px solid rgba(150, 200, 255, 0.34);
  background: rgba(150, 200, 255, 0.1);
  color: #cfe2ff;
  font: inherit;
  cursor: pointer;
}
.arena-leave[hidden] { display: none; }

.arena-ask {
  position: fixed;
  left: 50%;
  top: 36%;
  transform: translateX(-50%);
  z-index: 63;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.95);
  border: 1px solid rgba(255, 180, 120, 0.34);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  font: 13px/1.5 system-ui, sans-serif;
  color: #d8dee6;
  text-align: center;
}
.arena-ask[hidden] { display: none; }
.arena-ask-text { margin-bottom: 2px; }
.arena-ask-terms { color: #8fa0b4; font-size: 11px; margin-bottom: 8px; }
.arena-ask-buttons { display: flex; gap: 8px; }
.arena-ask-button {
  flex: 1;
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid rgba(150, 200, 255, 0.34);
  background: rgba(150, 200, 255, 0.1);
  color: #cfe2ff;
  font: inherit;
  cursor: pointer;
}
.arena-ask-button:hover:not(:disabled) { background: rgba(150, 200, 255, 0.2); }
.arena-ask-button:disabled { opacity: 0.4; cursor: default; }

.arena-result {
  position: fixed;
  left: 50%;
  top: 30%;
  transform: translateX(-50%);
  z-index: 62;
  padding: 14px 22px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.95);
  border: 1px solid rgba(255, 180, 120, 0.34);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  font: 14px/1.5 system-ui, sans-serif;
  color: #d8dee6;
  text-align: center;
}
.arena-result[hidden] { display: none; }
.arena-result-verdict { font-size: 16px; margin-bottom: 2px; }
.arena-result-you { color: #ffd27a; }
.arena-result-close {
  margin-top: 8px;
  padding: 3px 16px;
  border-radius: 6px;
  border: 1px solid rgba(150, 200, 255, 0.34);
  background: rgba(150, 200, 255, 0.1);
  color: #cfe2ff;
  font: inherit;
  cursor: pointer;
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class ArenaPanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly nameA: HTMLElement;
  private readonly winsA: HTMLElement;
  private readonly nameB: HTMLElement;
  private readonly winsB: HTMLElement;
  private readonly note: HTMLElement;
  private readonly leave: HTMLButtonElement;

  private readonly ask: HTMLElement;
  private readonly askText: HTMLElement;
  private readonly askTerms: HTMLElement;
  private readonly accept: HTMLButtonElement;
  private readonly refuse: HTMLButtonElement;

  private readonly result: HTMLElement;
  private readonly verdict: HTMLElement;
  private readonly verdictNote: HTMLElement;

  /** This client's own entity id, so the panel can find itself in a symmetric
   *  frame. Zero until the connection knows. */
  private selfId = 0;

  private drawn: ArenaInvitation | null = null;

  /** The token this panel has already answered, so a second click on one
   *  prompt cannot become a second answer. -1 when it has answered nothing. */
  private answered = -1;

  /** The match on screen, or null. Kept only so `describeResult` has somebody
   *  to compare a winner against; nothing about the score is computed here. */
  private duel: ArenaDuel | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: ArenaActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "arena");
    this.root.hidden = true;
    this.title = elem(doc, "div", "arena-title", "Duel");

    const score = elem(doc, "div", "arena-score");
    this.nameA = elem(doc, "span", "arena-fighter");
    this.winsA = elem(doc, "span", "arena-wins");
    const versus = elem(doc, "span", "arena-versus", ":");
    this.winsB = elem(doc, "span", "arena-wins");
    this.nameB = elem(doc, "span", "arena-fighter");
    score.append(this.nameA, this.winsA, versus, this.winsB, this.nameB);

    this.note = elem(doc, "div", "arena-note");
    this.leave = doc.createElement("button");
    this.leave.type = "button";
    this.leave.className = "arena-leave";
    this.leave.textContent = "Stop watching";
    this.leave.hidden = true;
    this.leave.addEventListener("click", () => this.actions.onStopWatching?.());
    this.root.append(this.title, score, this.note, this.leave);

    this.ask = elem(doc, "div", "arena-ask");
    this.ask.hidden = true;
    this.askText = elem(doc, "div", "arena-ask-text");
    this.askTerms = elem(doc, "div", "arena-ask-terms");
    const buttons = elem(doc, "div", "arena-ask-buttons");
    this.accept = doc.createElement("button");
    this.accept.type = "button";
    this.accept.className = "arena-ask-button";
    this.accept.textContent = "Fight";
    this.accept.addEventListener("click", () => this.answer(true));
    this.refuse = doc.createElement("button");
    this.refuse.type = "button";
    this.refuse.className = "arena-ask-button";
    this.refuse.textContent = "No";
    this.refuse.addEventListener("click", () => this.answer(false));
    buttons.append(this.accept, this.refuse);
    this.ask.append(this.askText, this.askTerms, buttons);

    this.result = elem(doc, "div", "arena-result");
    this.result.hidden = true;
    this.verdict = elem(doc, "div", "arena-result-verdict");
    this.verdictNote = elem(doc, "div", "arena-note");
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "arena-result-close";
    close.textContent = "Close";
    close.addEventListener("click", () => {
      this.result.hidden = true;
    });
    this.result.append(this.verdict, this.verdictNote, close);

    container.append(this.root, this.ask, this.result);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get asking(): boolean {
    return !this.ask.hidden;
  }

  get reporting(): boolean {
    return !this.result.hidden;
  }

  /** The token Accept would send. -1 when no prompt is drawn. For tests. */
  get shownToken(): number {
    return this.drawn === null ? -1 : this.drawn.token;
  }

  /** Which duel is on screen, or 0. For tests. */
  get shownDuel(): number {
    return this.duel === null ? 0 : this.duel.duel_id;
  }

  setSelf(entityId: number): void {
    this.selfId = entityId;
    if (this.duel !== null) this.update(this.duel);
  }

  invited(invitation: ArenaInvitation): void {
    if (invitation.token === 0) {
      this.drawn = null;
      this.ask.hidden = true;
      return;
    }
    this.drawn = invitation;
    const who = invitation.challenger_name || "Somebody";
    this.askText.textContent = `${who} challenges you to a duel.`;
    // The terms, spelled out, because they are what the message deliberately
    // does not let a challenger choose and what a player is actually agreeing
    // to. "First to 1" is a real answer -- the original's own `/duel a b 0`
    // starts a match one kill ends -- so it is not special-cased away.
    this.askTerms.textContent =
      `First to ${invitation.rounds}. ` +
      `${invitation.seconds} seconds to answer.`;
    this.setAnswerable(true);
    this.ask.hidden = false;
  }

  update(duel: ArenaDuel): void {
    if (duel.duel_id === 0) {
      this.duel = null;
      this.root.hidden = true;
      this.leave.hidden = true;
      return;
    }
    this.duel = duel;
    const watching = duel.identity === IDENTITY_OBSERVER;
    this.title.textContent = watching ? "Watching a duel" : "Duel";
    this.nameA.textContent = duel.a_name || "?";
    this.nameB.textContent = duel.b_name || "?";
    this.winsA.textContent = String(duel.a_wins);
    this.winsB.textContent = String(duel.b_wins);
    // Highlighted by id, never by position: `a` is whoever the server put
    // there, which for a challenge is the challenger and for the accepter is
    // the other column.
    this.nameA.classList.toggle(
      "arena-fighter-you",
      !watching && duel.a === this.selfId,
    );
    this.nameB.classList.toggle(
      "arena-fighter-you",
      !watching && duel.b === this.selfId,
    );
    this.note.textContent =
      duel.round_break > 0
        ? `Next round in ${duel.round_break}s`
        : `First to ${duel.rounds_to_win} · ${clock(duel.seconds_left)} left`;
    this.note.classList.toggle("arena-break", duel.round_break > 0);
    this.leave.hidden = !watching;
    this.root.hidden = false;
  }

  finished(result: ArenaResult): void {
    this.verdict.textContent = describeResult(result);
    const mine =
      result.outcome === ARENA_WON && result.winner !== 0 && result.winner === this.selfId;
    this.verdict.classList.toggle("arena-result-you", mine);
    this.verdictNote.textContent =
      result.outcome === ARENA_WON
        ? "Nothing changes hands: a duel pays no gold, no item and no experience."
        : "";
    this.result.hidden = false;
  }

  private answer(accept: boolean): void {
    const drawn = this.drawn;
    // The token of what is on screen. Never the connection's current
    // challenge: if a second one has arrived since this was drawn, the server
    // refuses this token, and refusing is the correct outcome -- the player
    // agreed to what they read, and what they read was a name and a format.
    if (drawn === null || drawn.token === this.answered) return;
    this.answered = drawn.token;
    this.setAnswerable(false);
    this.actions.onAnswer?.(drawn.token, accept);
  }

  private setAnswerable(can: boolean): void {
    this.accept.disabled = !can;
    this.refuse.disabled = !can;
  }
}
